/**
 * Shared formatting utilities for tool display.
 *
 * Used by both ToolCallBlock and NestedToolCallBlock for consistent
 * display of tool names, parameters, and results.
 */

/**
 * Format tool name from snake_case to Title Case.
 */
export function formatToolName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Try to decode a base64 string.
 *
 * @param str - Potential base64 string
 * @returns Decoded string if valid base64 with printable ASCII, null otherwise
 */
function tryDecodeBase64(str: string): string | null {
  // Must be at least 20 chars to be worth decoding, and match base64 pattern
  if (str.length < 20 || !/^[A-Za-z0-9+/]+=*$/.test(str)) {
    return null;
  }
  try {
    const decoded = atob(str);
    // Check if decoded string is printable ASCII (no control chars except space)
    if (/^[\x20-\x7E]+$/.test(decoded)) {
      return decoded;
    }
  } catch {
    // Not valid base64
  }
  return null;
}

/**
 * Format content for display, decoding base64 where found.
 *
 * Handles both string and object inputs, and adds decoded base64 comments
 * for GSL IDs (e.g., "R1NMVGVhbTo..." becomes "R1NMVGVhbTo..." / * GSLTeam:12345 * /)
 *
 * @param input - Content to format (string or object)
 * @returns Formatted string with decoded base64 comments
 */
export function formatContent(input: unknown): string {
  if (!input) return '';
  try {
    // If it's a string, try to parse it as JSON first
    const obj = typeof input === 'string' ? JSON.parse(input) : input;

    // Custom replacer that adds base64 decoded comments
    const formatted = JSON.stringify(obj, null, 2);

    // Find base64-looking strings and add decoded comments
    return formatted.replace(
      /"([A-Za-z0-9+/]{20,}=*)"/g,
      (match, b64String) => {
        const decoded = tryDecodeBase64(b64String);
        if (decoded) {
          return `"${b64String}" /* ${decoded} */`;
        }
        return match;
      }
    );
  } catch {
    // If parsing fails, return as-is
    return String(input);
  }
}
